import processEvent from 'analytics/processEvent';
import anaConsts from 'analytics/constants';
import anaUtils from 'analytics/utils';
import {
    PRODUCT_RECOMMENDATION_ANALYTICS_CATEGORY,
    COMPLEMENTARY_CAROUSEL_ANALYTICS_NAME,
    COMPLEMENTARY_CAROUSEL_ANALYTICS_SLUG
} from 'ai/constants/superchat';

export function trackAddToBasket(skuId, options = {}) {
    const { useNextPageData = false } = options;

    const data = {
        pageName: anaConsts.SUPER_CHAT.SUPERCHAT_LANDING,
        actionInfo: anaConsts.SUPER_CHAT.SUPERCHAT_BASKET,
        eventStrings: 'scAdd',
        productStrings: `;${skuId};;;;eVar26=${skuId}|eVar23=superchat|eVar72=superchat|eVar133=standard`
    };

    if (useNextPageData) {
        anaUtils.setNextPageData(data);
    } else {
        processEvent.process(anaConsts.LINK_TRACKING_EVENT, { data });
    }
}

/**
 * Stores recommendation referrer context so the destination Product Detail Page (PDP)
 * page-load call (s.t() / TrackState) attributes the visit to the SuperChat
 * "Works well with…" complementary carousel. Implements AC2 of GAI-6812.
 *
 * Must be called right before navigating to the PDP. The PDP page-load bindings
 * (productPageBindings / pageLoadEvent) consume this data to populate:
 *   - prop6 (previousPage): `superchat:landing:n/a:*`
 *   - events: `event269` (in addition to the PDP's own `prodView`, `event24`)
 *   - eVar75 (internalCampaign): `<carouselName>:product` (e.g. `works well with:product`)
 *   - products eVar52: `sephora_superchat_<carouselName>_n/a`
 *   - products eVar69: `<carouselName>` (forced via useSimplifiedCarouselTracking)
 *
 * @returns {void}
 */
export function setComplementaryProductClickData() {
    const carouselName = COMPLEMENTARY_CAROUSEL_ANALYTICS_NAME;

    anaUtils.setNextPageData({
        pageName: anaConsts.SUPER_CHAT.SUPERCHAT_LANDING,
        pageType: anaConsts.SUPER_CHAT.SUPERCHAT_EVAR,
        recInfo: {
            isExternalRec: false,
            componentTitle: carouselName,
            podId: 'n/a',
            useSimplifiedCarouselTracking: true
        },
        internalCampaign: `${carouselName}:product`,
        events: [anaConsts.Event.EVENT_269]
    });
}

export function trackAddToLoves(skuId) {
    processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
        data: {
            pageName: anaConsts.SUPER_CHAT.SUPERCHAT_LANDING,
            eventStrings: anaConsts.Event.EVENT_27,
            productStrings: `;${skuId};;;;eVar26=${skuId}`
        }
    });
}

export function trackRemoveFromLoves(skuId) {
    processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
        data: {
            pageName: anaConsts.SUPER_CHAT.SUPERCHAT_LANDING,
            eventStrings: anaConsts.Event.EVENT_28,
            productStrings: `;${skuId};;;;eVar26=${skuId}`
        }
    });
}

export function trackPurchaseHistoryCardView() {
    processEvent.process(anaConsts.ASYNC_PAGE_LOAD, {
        data: {
            pageName: anaConsts.SUPER_CHAT.SUPERCHAT_PURCHASE_HISTORY
        }
    });
}

export function trackPurchaseHistoryAddToBasket(skuId, options = {}) {
    const { fulfillmentType = 'standard', useNextPageData = false } = options;

    const data = {
        pageName: anaConsts.SUPER_CHAT.SUPERCHAT_PURCHASE_HISTORY,
        actionInfo: anaConsts.SUPER_CHAT.SUPERCHAT_PURCHASE_HISTORY_BASKET,
        eventStrings: 'scAdd',
        productStrings: `;${skuId};;;;eVar26=${skuId}|eVar23=superchat|eVar72=superchat|eVar133=${fulfillmentType}`
    };

    if (useNextPageData) {
        anaUtils.setNextPageData(data);
    } else {
        processEvent.process(anaConsts.LINK_TRACKING_EVENT, { data });
    }
}

/**
 * Fires an Adobe Analytics async page load (s.t() / TrackState) when a SuperChat
 * Loyalty card is rendered. Sets `pageName` to the loyalty page identifier per AC1
 * of GAI-6110. Should be called once per card render (caller is responsible for guarding).
 *
 * @param {('birthday'|'rewardsBazaar')} rewardType - Reward card type. Drives `pageName`:
 *   `birthday` => `superchat:loyalty:birthday gift:*`,
 *   anything else => `superchat:loyalty:reward bazaar:*`.
 * @returns {void}
 */
export function trackRewardCardView(rewardType) {
    const pageName = rewardType === 'birthday' ? anaConsts.SUPER_CHAT.SUPERCHAT_BIRTHDAY_REWARD : anaConsts.SUPER_CHAT.SUPERCHAT_REWARDS_BAZAAR;

    processEvent.process(anaConsts.ASYNC_PAGE_LOAD, {
        data: { pageName }
    });
}

/**
 * Fires an Adobe Analytics link tracking event (s.tl() / TrackAction) when a user
 * adds a Loyalty card item to basket. Implements AC2 of GAI-6110.
 *
 * Adobe variables set:
 *   - pageName / eVar70 / eVar97: loyalty page identifier (same as card view)
 *   - prop55 (actionInfo): `reward bazaar:add-to-basket` (intentionally identical for both
 *     reward types — confirmed with analytics owner)
 *   - events: `scAdd`
 *   - products: `;<sku>;;;;eVar26=<sku>|eVar23=superchat|eVar133=standard|eVar72=superchat`
 *   - eVar61 (biRewardType): the reward identifier (e.g., `100 Points` or `Birthday Gift`)
 *
 * @param {string} skuId - SKU id of the reward item being added.
 * @param {('birthday'|'rewardsBazaar')} rewardType - Reward card type that fired the event.
 * @param {string} biRewardType - Reward identifier passed to eVar61
 *   (e.g., `100 Points` for Reward Bazaar, `Birthday Gift` for Birthday).
 * @returns {void}
 */
export function trackRewardAddToBasket(skuId, rewardType, biRewardType) {
    const isBirthday = rewardType === 'birthday';

    processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
        data: {
            pageName: isBirthday ? anaConsts.SUPER_CHAT.SUPERCHAT_BIRTHDAY_REWARD : anaConsts.SUPER_CHAT.SUPERCHAT_REWARDS_BAZAAR,
            actionInfo: isBirthday ? anaConsts.SUPER_CHAT.SUPERCHAT_BIRTHDAY_REWARD_BASKET : anaConsts.SUPER_CHAT.SUPERCHAT_REWARDS_BAZAAR_BASKET,
            eventStrings: 'scAdd',
            productStrings: `;${skuId};;;;eVar26=${skuId}|eVar23=superchat|eVar133=standard|eVar72=superchat`,
            biRewardType
        }
    });
}

export function trackPromoCardView() {
    processEvent.process(anaConsts.ASYNC_PAGE_LOAD, {
        data: { pageName: anaConsts.SUPER_CHAT.SUPERCHAT_BEAUTY_OFFERS }
    });
}

export function buildBasketAnalyticsData(sku, options = {}) {
    const { sotV425 = null, isPurchaseHistory = false, isComplementary = false } = options;

    // GAI-6812 AC1: add-to-basket from the "Works well with…" complementary carousel needs a
    // distinct prop55 (`add to basket:superchat-<slug>`). The complementary-specific events
    // (event269), eVar69, eVar72 (`superchat-<slug>`) and eVar75 are applied in addToBasketEvent.js
    // via the `fromComplementaryCarousel` flag.
    // Complementary takes precedence over purchase-history so a caller passing both flags never
    // yields a mixed context (purchase-history pageName with complementary action info).
    const isPurchaseHistoryAdd = isPurchaseHistory && !isComplementary;
    const standardActionInfo = isPurchaseHistoryAdd ? anaConsts.SUPER_CHAT.SUPERCHAT_PURCHASE_HISTORY_BASKET : anaConsts.SUPER_CHAT.SUPERCHAT_BASKET;

    return {
        productId: sku.productId,
        brandName: sku.brand_name,
        productName: sku.title,
        category: PRODUCT_RECOMMENDATION_ANALYTICS_CATEGORY,
        pageName: isPurchaseHistoryAdd ? anaConsts.SUPER_CHAT.SUPERCHAT_PURCHASE_HISTORY : anaConsts.SUPER_CHAT.SUPERCHAT_LANDING,
        customActionInfo: isComplementary ? `add to basket:superchat-${COMPLEMENTARY_CAROUSEL_ANALYTICS_SLUG}` : standardActionInfo,
        productStrings: [`;${sku.skuId};;;;eVar26=${sku.skuId}|eVar23=superchat|eVar133=standard`],
        fromSuperChatModal: true,
        ...(isComplementary && { fromComplementaryCarousel: true }),
        ...(sotV425 && { sotV425 })
    };
}
